import { useParams } from 'react-router-dom';
import { type CSSProperties, useEffect, useRef, useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/shared/components/ui/button';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/shared/components/ui/alert-dialog';
import { dbg, dbgGroup } from '@/lib/rec-debug';
import { PostCallReferralPrompt } from '@/components/PostCallReferralPrompt';
import { Mic, MicOff, Video, VideoOff, Settings, CheckCircle, Check, RotateCw, SwitchCamera } from 'lucide-react';
import { useScreenOrientation } from '@/shared/hooks/useScreenOrientation';
import { useToast } from '@/shared/hooks/use-toast';

const PREFIX = 'RecordSubmission';
const RECDBG =
  typeof window !== 'undefined' &&
  (new URLSearchParams(window.location.search).get('recDebug') === '1' ||
    new URLSearchParams(window.location.search).get('debug') === '1' ||
    localStorage.getItem('recDebug') === '1');

/** objectFit for recording preview: 'contain' (default) or 'cover' for full-bleed */
const RECORD_PREVIEW_OBJECT_FIT: 'contain' | 'cover' = 'contain';

const getVp = (): { w: number; h: number } => {
  if (typeof window === 'undefined') return { w: 0, h: 0 };
  return {
    w: window.visualViewport?.width ?? window.innerWidth,
    h: window.visualViewport?.height ?? window.innerHeight,
  };
};

export default function RecordSubmissionPage() {
  const { token } = useParams();
  const { toast } = useToast();

  const sessionIdRef = useRef((typeof crypto !== 'undefined' && crypto.randomUUID?.()) ?? String(Math.random()).slice(2));
  const sessionId = sessionIdRef.current;

  const log = (tag: string, data?: unknown) => {
    if (!RECDBG) return;
    console.log(`[RECDbg][RecordSubmission][${sessionId}][${Math.round(performance.now())}ms] ${tag}`, data ?? '');
  };
  const warn = (tag: string, data?: unknown) => {
    if (!RECDBG) return;
    console.warn(`[RECDbg][RecordSubmission][${sessionId}][${Math.round(performance.now())}ms] ${tag}`, data ?? '');
  };
  const errLog = (tag: string, data?: unknown) => {
    if (!RECDBG) return;
    console.error(`[RECDbg][RecordSubmission][${sessionId}][${Math.round(performance.now())}ms] ${tag}`, data ?? '');
  };

  /** Snap angle to 0/90/180/270 for rotation (matches live-call useRecording). */
  const snapTo90 = (angle: number): number => {
    const n = ((angle % 360) + 360) % 360;
    return Math.round(n / 90) * 90 % 360;
  };

  // ✅ Screen orientation hook - aligned with live call recordings (useScreenOrientation + usePhysicalOrientation)
  const { angle: rotationAngle, isLandscape } = useScreenOrientation({
    debounceMs: 150,
    usePhysicalOrientation: true,
  });

  const [loading, setLoading] = useState(true);
  const [roomId, setRoomId] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const videoRef = useRef<HTMLVideoElement>(null);
  const recordedVideoRef = useRef<HTMLVideoElement | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const recRef = useRef<MediaRecorder | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const mimeRef = useRef<{ mime: string; ext: string; content: string } | null>(null);
  const activeStreamRef = useRef<MediaStream | null>(null); // ✅ Track active stream for cleanup

  // Canvas-based recording: record from canvas.captureStream() so camera flips don't restart MediaRecorder.
  // This avoids MP4 segment concatenation issues on iOS Safari.
  const lastOrientationLogRef = useRef<{ rotationAngle: number; isLandscape: boolean } | null>(null);
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const drawLoopRef = useRef<number | null>(null);
  const facingModeRef = useRef<'environment' | 'user'>('environment'); // For draw loop (mirror front camera)
  const recordingAudioRef = useRef<MediaStreamTrack | null>(null); // Stable audio for entire recording
  const rotationAngleRef = useRef<number>(0); // Current device rotation, updated every frame (live-call style)
  const lastDrawLoopLogTsRef = useRef<number>(0);
  const [recording, setRecording] = useState(false);

  // iOS Safari orientation stabilization: fullscreen/viewport changes cause transient angle flips (90→0→90).
  // Lock to base angle for 800ms, then hysteresis (250ms) so brief wobbles don't feed into the canvas.
  // Prevents "momentary crammed portrait" and sideways playback from unstable rotations.
  const baseAngleRef = useRef<number | null>(null);
  const stableAngleRef = useRef<number>(0);
  const pendingAngleRef = useRef<number>(0);
  const pendingSinceRef = useRef<number>(0);
  const startedAtRef = useRef<number>(0);

  const getRects = () => {
    const c = containerRef.current?.getBoundingClientRect();
    const v = videoRef.current?.getBoundingClientRect();
    return {
      containerRect: c ? { w: c.width, h: c.height } : null,
      videoRect: v ? { w: v.width, h: v.height } : null,
    };
  };
  const getVideoComputed = (el?: HTMLVideoElement | null) => {
    const videoEl = el ?? videoRef.current;
    if (!videoEl) return null;
    const s = getComputedStyle(videoEl);
    return { transform: s.transform, objectFit: s.objectFit, width: s.width, height: s.height };
  };
  // ✅ FIX: Start with back camera (environment) for guest recordings
  const [facingMode, setFacingMode] = useState<'environment' | 'user'>('environment');
  const [micOn, setMicOn] = useState(true);
  const [camOn, setCamOn] = useState(true);
  const [videoDevices, setVideoDevices] = useState<MediaDeviceInfo[]>([]);
  const [audioDevices, setAudioDevices] = useState<MediaDeviceInfo[]>([]);
  const [videoDeviceId, setVideoDeviceId] = useState('');
  const [audioDeviceId, setAudioDeviceId] = useState('');
  const [recordedBlob, setRecordedBlob] = useState<Blob | null>(null);
  const [recordedUrl, setRecordedUrl] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [showDeviceSettings, setShowDeviceSettings] = useState(false);
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [recordingDuration, setRecordingDuration] = useState(0);
  const timerIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const [isRecordingViewActive, setIsRecordingViewActive] = useState(false);
  const [recordViewport, setRecordViewport] = useState<{ w: number; h: number }>(() => getVp());
  const [submissionSuccess, setSubmissionSuccess] = useState(false);
  const [showReferralPrompt, setShowReferralPrompt] = useState(false);
  const [guestInfo, setGuestInfo] = useState<{ name?: string; email?: string; phone?: string }>({});
  const [showRecordAgainConfirm, setShowRecordAgainConfirm] = useState(false);

  // User's chosen recording layout — determines canvas dimensions at recording start.
  // 'portrait' = tall video (default), 'landscape' = wide video.
  const [recordingLayout, setRecordingLayout] = useState<'portrait' | 'landscape'>('portrait');

  // B1) Orientation debug — log when rotationAngle or isLandscape changes (throttled)
  useEffect(() => {
    const prev = lastOrientationLogRef.current;
    if (prev && prev.rotationAngle === rotationAngle && prev.isLandscape === isLandscape) return;
    lastOrientationLogRef.current = { rotationAngle, isLandscape };
    const screenAngle = (window.screen as any).orientation?.angle;
    dbg(PREFIX, 'orientation', {
      rotationAngle,
      isLandscape,
      screenAngle: screenAngle ?? 'n/a',
      windowInner: { w: window.innerWidth, h: window.innerHeight },
      visualViewport: window.visualViewport
        ? { w: window.visualViewport.width, h: window.visualViewport.height, scale: window.visualViewport.scale }
        : 'n/a',
    });
  }, [rotationAngle, isLandscape]);

  useEffect(() => {
    (async () => {
      try {
        // Ensure auth (anonymous OK) before any Storage calls
        const s = await supabase.auth.getSession();
        if (!s.data.session) await (supabase as any).auth.signInAnonymously();

        let resolvedRoom: string | null = null;
        const directFlag = new URLSearchParams(window.location.search).get('direct');

        // If this is a direct link, skip backend lookup entirely
        if (directFlag === '1' || (token && token.startsWith('room_'))) {
          resolvedRoom = token as string;
        } else {
          // Try to validate token with backend; swallow errors (just affects console noise)
          try {
            const base = import.meta.env.VITE_SIGNALING_SERVER_URL ?? 'http://localhost:3000';
            const resp = await fetch(`${base}/recording-requests/${token}`);
            if (resp.ok) {
              const json = await resp.json();
              if (json?.ok && json.roomId) resolvedRoom = json.roomId as string;
            }
          } catch {
            // ignore; fallback below will handle
          }
        }

        if (!resolvedRoom) throw new Error('Invalid or expired link');
        setRoomId(resolvedRoom);

        // Check for persisted recording from localStorage
        try {
          const savedRecording = localStorage.getItem(`recording_${resolvedRoom}_${token}`);
          if (savedRecording) {
            // Restore recording from localStorage
            const response = await fetch(savedRecording);
            const blob = await response.blob();
            setRecordedBlob(blob);
            setRecordedUrl(savedRecording);
            console.log('Restored recording from localStorage');
          }
        } catch (e) {
          console.warn('Could not restore recording from localStorage:', e);
        }

        // Prepare preview stream (quality guard + logging for iOS back camera)
        const stream = await getUserMediaWithQualityGuard('environment', '', audioDeviceId || '', 'initial load');
        activeStreamRef.current = stream; // ✅ Store active stream for cleanup
        applyTrackStates(stream);

        // ✅ FIX: Update facingMode from actual camera settings on initial load
        try {
          const videoTrack = stream.getVideoTracks()[0];
          const settings: any = videoTrack?.getSettings?.() || {};
          if (settings.facingMode === 'user' || settings.facingMode === 'environment') {
            console.log(`✅ Initial camera facingMode: ${settings.facingMode}`);
            setFacingMode(settings.facingMode);
          }
        } catch (err) {
          console.warn('Could not read initial camera settings:', err);
        }

        if (videoRef.current) {
          videoRef.current.pause?.();
          videoRef.current.removeAttribute('src');
          // @ts-ignore
          videoRef.current.src = '';
          videoRef.current.srcObject = stream;
          videoRef.current.controls = false;
          videoRef.current.muted = true;
          videoRef.current.playsInline = true;
          videoRef.current.autoplay = true; // Ensure autoplay is set

          // Ensure video actually plays before proceeding
          try {
            await videoRef.current.play();
            console.log('[RecordSubmission] Initial camera stream started successfully');
          } catch (playError) {
            console.warn('[RecordSubmission] Initial play failed:', playError);
            // Try again after a small delay
            await new Promise(resolve => setTimeout(resolve, 100));
            await videoRef.current.play().catch(e => {
              console.error('[RecordSubmission] Second play attempt failed:', e);
            });
          }

          // Force a layout repaint to ensure video is visible
          videoRef.current.style.display = 'block';
        }
        await refreshDeviceLists();
      } catch (e: any) {
        setErr(e?.message || String(e));
      } finally {
        setLoading(false);
      }
    })();
    return () => {
      const el = videoRef.current as any;
      const st: MediaStream | null = el?.srcObject || null;
      st?.getTracks().forEach((t) => t.stop());
    };
  }, [token]);

  const pickType = () => {
    // Prefer MP4 on Safari/iOS, then WebM elsewhere. As a final fallback,
    // allow the browser default by returning empty mime (we will try both).
    const opts = [
      { mime: 'video/mp4;codecs=avc1.42E01E,mp4a.40.2', ext: 'mp4', content: 'video/mp4' },
      { mime: 'video/webm;codecs=vp8,opus', ext: 'webm', content: 'video/webm' },
    ];
    // @ts-ignore
    if (typeof MediaRecorder !== 'undefined') {
      for (const o of opts) {
        // @ts-ignore
        try { if (MediaRecorder.isTypeSupported(o.mime)) return o; } catch {}
      }
    }
    return { mime: '', ext: 'mp4', content: 'video/mp4' };
  };

  // ——— iOS back camera quality + FOV ———
  // Why aspectRatio made FOV tighter: Safari (and some devices) satisfy aspectRatio by center-cropping the
  // sensor output, so we get a narrower field of view. Removing aspectRatio lets the camera deliver native FOV.
  // Why Safari delivered lower resolution: with strict or ideal aspectRatio + high width/height, some iOS
  // builds fall back to a lower-res mode or a different pipeline; requesting 1920x1080 without aspectRatio
  // and retrying with 1280x720 if delivery is below guard avoids that.
  const QUALITY_MIN_WIDTH = 1000;
  const QUALITY_MIN_HEIGHT = 700;

  /** Log track settings/constraints after getUserMedia to diagnose iOS FOV/resolution. Returns delivered width/height. */
  const logTrackQuality = (stream: MediaStream, context: string): { width: number; height: number } => {
    const track = stream.getVideoTracks()[0];
    let width = 0, height = 0;
    if (track) {
      try {
        const settings = track.getSettings?.() || {};
        const constraints = track.getConstraints?.() || {};
        width = settings.width ?? 0;
        height = settings.height ?? 0;
        console.log(`[RecordSubmission] 📷 ${context} — getSettings():`, {
          width: settings.width,
          height: settings.height,
          aspectRatio: settings.aspectRatio,
          frameRate: settings.frameRate,
          facingMode: settings.facingMode,
          deviceId: settings.deviceId ? `${String(settings.deviceId).slice(0, 8)}…` : undefined,
        });
        console.log(`[RecordSubmission] 📷 ${context} — getConstraints():`, constraints);
        if (width < QUALITY_MIN_WIDTH || height < QUALITY_MIN_HEIGHT) {
          console.warn(`[RecordSubmission] ⚠️ ${context} — delivered resolution ${width}x${height} below guard (${QUALITY_MIN_WIDTH}x${QUALITY_MIN_HEIGHT}), may retry with fallback.`);
        }
      } catch (e) {
        console.warn('[RecordSubmission] logTrackQuality failed:', e);
      }
    }
    return { width, height };
  };

  /** Video constraints: no aspectRatio (Safari can crop to satisfy it → tighter FOV). Primary = 1080p, fallback = 720p. */
  const getPrimaryVideoConstraints = (): MediaTrackConstraints => {
    const base: MediaTrackConstraints = {
      width: { ideal: 1920, max: 3840 },
      height: { ideal: 1080, max: 2160 },
      frameRate: { ideal: 30, max: 60 },
    };
    try {
      const supported = (navigator.mediaDevices as any).getSupportedConstraints?.();
      if (supported?.resizeMode) (base as any).resizeMode = 'none';
    } catch {}
    return base;
  };
  const getFallbackVideoConstraints = (): MediaTrackConstraints => ({
    width: { ideal: 1280, max: 1920 },
    height: { ideal: 720, max: 1080 },
    frameRate: { ideal: 30, max: 60 },
  });

  /**
   * getUserMedia with quality guard and logging. Used for initial load, switchCamera, ensurePreviewStream, reRecord.
   * If delivered resolution is below QUALITY_MIN_WIDTH/HEIGHT, retries once with fallback (1280x720) constraints.
   */
  const getUserMediaWithQualityGuard = async (
    preferFacing: 'environment' | 'user',
    videoDeviceIdOverride: string,
    audioDeviceIdOverride: string,
    context: string
  ): Promise<MediaStream> => {
    const audioConstraint = audioDeviceIdOverride
      ? { deviceId: { exact: audioDeviceIdOverride } }
      : true;
    const videoPrimary: MediaTrackConstraints = videoDeviceIdOverride
      ? { ...getPrimaryVideoConstraints(), deviceId: { exact: videoDeviceIdOverride } }
      : { ...getPrimaryVideoConstraints(), facingMode: { ideal: preferFacing } };
    const videoFallback: MediaTrackConstraints = videoDeviceIdOverride
      ? { ...getFallbackVideoConstraints(), deviceId: { exact: videoDeviceIdOverride } }
      : { ...getFallbackVideoConstraints(), facingMode: { ideal: preferFacing } };

    let stream = await navigator.mediaDevices.getUserMedia({ video: videoPrimary, audio: audioConstraint });
    const { width, height } = logTrackQuality(stream, context);
    const vt = stream.getVideoTracks()[0];
    log('gum_ok', {
      context,
      settings: vt?.getSettings?.() ?? {},
      constraints: vt?.getConstraints?.() ?? {},
      trackReadyState: vt?.readyState,
      trackLabel: vt?.label?.slice(0, 30),
      deviceIdPrefix: vt?.getSettings?.()?.deviceId ? String((vt.getSettings() as any).deviceId).slice(0, 8) : undefined,
      ...getRects(),
    });

    if (width < QUALITY_MIN_WIDTH || height < QUALITY_MIN_HEIGHT) {
      console.log(`[RecordSubmission] 🔄 ${context} — retrying with fallback constraints (1280x720)`);
      stream.getTracks().forEach((t) => t.stop());
      stream = await navigator.mediaDevices.getUserMedia({ video: videoFallback, audio: audioConstraint });
      logTrackQuality(stream, `${context} (fallback)`);
      const vt2 = stream.getVideoTracks()[0];
      log('gum_ok', {
        context: `${context} (fallback)`,
        settings: vt2?.getSettings?.() ?? {},
        constraints: vt2?.getConstraints?.() ?? {},
        trackReadyState: vt2?.readyState,
        trackLabel: vt2?.label?.slice(0, 30),
        deviceIdPrefix: vt2?.getSettings?.()?.deviceId ? String((vt2.getSettings() as any).deviceId).slice(0, 8) : undefined,
        ...getRects(),
      });
    }
    return stream;
  };

  const buildConstraints = (preferFacing: 'environment' | 'user' = facingMode): MediaStreamConstraints => {
    // No aspectRatio: on iOS Safari it can cause center-crop or low-res fallback → tighter FOV and softer image.
    const baseVideo = videoDeviceId
      ? { ...getPrimaryVideoConstraints(), deviceId: { exact: videoDeviceId } }
      : { ...getPrimaryVideoConstraints(), facingMode: { ideal: preferFacing } };
    const audioConstraint = audioDeviceId
      ? { deviceId: { exact: audioDeviceId } }
      : true;
    return { video: baseVideo, audio: audioConstraint };
  };

  const applyTrackStates = (stream: MediaStream) => {
    stream.getAudioTracks().forEach((track) => (track.enabled = micOn));
    stream.getVideoTracks().forEach((track) => (track.enabled = camOn));
  };

  const refreshDeviceLists = async () => {
    try {
      const devices = await navigator.mediaDevices.enumerateDevices();
      const vids = devices.filter((d) => d.kind === 'videoinput');
      const auds = devices.filter((d) => d.kind === 'audioinput');
      setVideoDevices(vids);
      setAudioDevices(auds);
      if (!videoDeviceId && vids.length) {
        // Prefer the true wide back camera when multiple back cameras exist (e.g. iPhone Ultra Wide vs Wide).
        // iOS may expose "Back Ultra Wide", "Back Camera" (wide), "Back Telephoto" — we want the main wide.
        const backCams = vids.filter((d) => /back|rear|environment/i.test(d.label || ''));
        const wideBack =
          backCams.length > 1
            ? backCams.find((d) => {
                const L = (d.label || '').toLowerCase();
                return !L.includes('ultra') && !L.includes('telephoto') && !L.includes('macro');
              }) ?? backCams[0]
            : backCams[0];
        const chosen = wideBack || vids[0];
        setVideoDeviceId(chosen.deviceId);
        setFacingMode(wideBack ? 'environment' : facingMode);
      }
      if (!audioDeviceId && auds[0]) setAudioDeviceId(auds[0].deviceId);
    } catch {}
  };

  const ensurePreviewStream = async (): Promise<MediaStream> => {
    const el = videoRef.current as any;
    let stream: MediaStream | null = el?.srcObject || null;
    const ended = (s: MediaStream | null) => !s || s.getTracks().every((tr) => tr.readyState === 'ended');
    if (!stream || ended(stream)) {
      console.log('[RecordSubmission] (re)acquiring media stream...');
      try {
        stream = await getUserMediaWithQualityGuard(facingMode, videoDeviceId, audioDeviceId, 'ensurePreviewStream');
        activeStreamRef.current = stream;
        applyTrackStates(stream);
      } catch (e1) {
        const attempts: MediaStreamConstraints[] = [
          buildConstraints(facingMode),
          { video: videoDeviceId ? { deviceId: { exact: videoDeviceId } } : true, audio: audioDeviceId ? { deviceId: { exact: audioDeviceId } } : true },
          { video: true, audio: true },
        ];
        let lastErr: any;
        for (const c of attempts) {
          try {
            stream = await navigator.mediaDevices.getUserMedia(c);
            activeStreamRef.current = stream;
            applyTrackStates(stream);
            logTrackQuality(stream, 'ensurePreviewStream (fallback)');
            break;
          } catch (e) {
    // // B2) Stream acquired — track settings, preview rect, computed styles
    // const vid = videoRef.current;
    // const tracks = stream.getTracks().map((t) => ({
    //   kind: t.kind,
    //   readyState: t.readyState,
    //   settings: t.getSettings?.() ?? {},
    // }));
    // dbg(PREFIX, 'ensurePreviewStream acquired', {
    //   streamId: stream.id,
    //   trackCount: stream.getTracks().length,
    //   tracks,
    //   previewRect: vid?.getBoundingClientRect()
    //     ? { w: vid.getBoundingClientRect().width, h: vid.getBoundingClientRect().height }
    //     : 'n/a',
    //   previewTransform: vid ? getComputedStyle(vid).transform : 'n/a',
    //   previewObjectFit: vid ? getComputedStyle(vid).objectFit : 'n/a',
    // });
            lastErr = e;
          }
        }
        if (!stream) throw lastErr || new Error('Unable to obtain media stream');
      }
      if (videoRef.current) {
        videoRef.current.pause?.();
        videoRef.current.removeAttribute('src');
        // @ts-ignore
        videoRef.current.src = '';
        videoRef.current.srcObject = stream;
        videoRef.current.controls = false;
        videoRef.current.muted = true;
        videoRef.current.playsInline = true;
        log('preview_attached', { streamId: stream.id, trackCount: stream.getTracks().length, rects: getRects(), computed: getVideoComputed() });
        try {
          await videoRef.current.play();
          log('preview_play', { readyState: videoRef.current.readyState, paused: videoRef.current.paused });
        } catch {}
    // B2) Stream acquired — track settings, preview rect, computed styles
    const vid = videoRef.current;
    const tracks = stream.getTracks().map((t) => ({
      kind: t.kind,
      readyState: t.readyState,
      settings: t.getSettings?.() ?? {},
    }));
    dbg(PREFIX, 'ensurePreviewStream acquired', {
      streamId: stream.id,
      trackCount: stream.getTracks().length,
      tracks,
      previewRect: vid?.getBoundingClientRect()
        ? { w: vid.getBoundingClientRect().width, h: vid.getBoundingClientRect().height }
        : 'n/a',
      previewTransform: vid ? getComputedStyle(vid).transform : 'n/a',
      previewObjectFit: vid ? getComputedStyle(vid).objectFit : 'n/a',
    });
      }
    }
    return stream!;
  };

  const switchCamera = async () => {
    const next: 'environment' | 'user' = facingMode === 'environment' ? 'user' : 'environment';
    console.log(`[RecordSubmission] 🔄 switchCamera — ${facingMode} → ${next}`);

    try {
      if (!recording) setVideoDeviceId('');

      const el = videoRef.current as any;
      const currentStream: MediaStream | null = el?.srcObject || null;

      if (recording) {
        // Canvas recording: request VIDEO ONLY. Recorder never stops — we only swap video.srcObject.
        // Use facingMode for flip (ignore deviceId so we get the other camera).
        const videoOnlyConstraints: MediaStreamConstraints = {
          video: { ...getPrimaryVideoConstraints(), facingMode: { ideal: next } },
          audio: false,
        };
        let newVideoStream: MediaStream | null = null;
        try {
          newVideoStream = await navigator.mediaDevices.getUserMedia(videoOnlyConstraints);
          logTrackQuality(newVideoStream, 'switchCamera (recording, video-only)');
        } catch (e1) {
          newVideoStream = await navigator.mediaDevices.getUserMedia({
            video: { facingMode: { ideal: next } },
            audio: false,
          });
          logTrackQuality(newVideoStream, 'switchCamera (recording, fallback)');
        }

        const newVideoTrack = newVideoStream.getVideoTracks()[0];
        if (!newVideoTrack) {
          newVideoStream.getTracks().forEach((t) => t.stop());
          throw new Error('Unable to switch camera');
        }

        try {
          const settings: any = newVideoTrack?.getSettings?.() || {};
          const actualFacingMode = settings.facingMode || next;
          console.log(`[RecordSubmission] ✅ Camera switched to: ${actualFacingMode} (recording — no recorder restart)`);
          setFacingMode(actualFacingMode);
        } catch {
          setFacingMode(next);
        }

        currentStream?.getVideoTracks().forEach((t) => t.stop());
        if (videoRef.current) {
          videoRef.current.srcObject = new MediaStream([newVideoTrack]);
          videoRef.current.muted = true;
          try { await videoRef.current.play(); } catch {} 
        }
      } else {
        // Not recording: full stream swap (video + audio)
        let newStream: MediaStream | null = null;
        try {
          newStream = await getUserMediaWithQualityGuard(next, '', audioDeviceId, 'switchCamera');
        } catch (e1) {
          const attempts: MediaStreamConstraints[] = [
            buildConstraints(next),
            { video: { facingMode: { ideal: next } }, audio: audioDeviceId ? { deviceId: { exact: audioDeviceId } } : true },
            { video: true, audio: audioDeviceId ? { deviceId: { exact: audioDeviceId } } : true },
          ];
          for (const c of attempts) {
            try {
              newStream = await navigator.mediaDevices.getUserMedia(c);
              logTrackQuality(newStream!, 'switchCamera (fallback)');
              break;
            } catch { newStream = null; }
          }
        }
        if (!newStream) throw new Error('Unable to switch camera');
        applyTrackStates(newStream);
        const newVideoTrack = newStream.getVideoTracks()[0];
        try {
          const settings: any = newVideoTrack?.getSettings?.() || {};
          setFacingMode(settings.facingMode || next);
        } catch { setFacingMode(next); }
        currentStream?.getTracks().forEach((t: MediaStreamTrack) => t.stop());
        if (videoRef.current) {
          videoRef.current.pause?.();
          videoRef.current.removeAttribute('src');
          (videoRef.current as any).src = '';
          videoRef.current.srcObject = newStream;
          videoRef.current.controls = false;
          videoRef.current.muted = true;
          await videoRef.current.play().catch(() => {});
        }
      }
    } catch (e) {
      console.warn('[RecordSubmission] switchCamera failed:', e);
    }
  };

  useEffect(() => {
    if (!videoRef.current) return;
    ensurePreviewStream().catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [videoDeviceId, audioDeviceId]);

  // D) Recording view state — log only when isLandscape, rotationAngle, or facingMode changes
  useEffect(() => {
    if (!isRecordingViewActive || recordedBlob) return;
    log('recording_view_state', { isLandscape, rotationAngle, facingMode });
  }, [isRecordingViewActive, recordedBlob, isLandscape, rotationAngle, facingMode]);

  // B3) When entering recording view — log container and video rect
  useEffect(() => {
    if (!isRecordingViewActive) return;
    const t = setTimeout(() => {
      log('entering_recording_view', { rects: getRects(), computed: getVideoComputed() });
    }, 100);
    return () => clearTimeout(t);
  }, [isRecordingViewActive]);

  // iOS Safari: track visualViewport for recording view so rotation reflows correctly
  useEffect(() => {
    if (!isRecordingViewActive) return;
    let rafId: number | null = null;
    const prevVpRef = { w: 0, h: 0 };
    const update = () => {
      const vp = getVp();
      if (vp.w !== prevVpRef.w || vp.h !== prevVpRef.h) {
        prevVpRef.w = vp.w;
        prevVpRef.h = vp.h;
        setRecordViewport(vp);
        log('record_viewport_update', {
          viewportPx: vp,
          visualViewport: window.visualViewport
            ? { w: window.visualViewport.width, h: window.visualViewport.height, scale: window.visualViewport.scale }
            : 'n/a',
          innerWidth: window.innerWidth,
          innerHeight: window.innerHeight,
        });
      }
    };
    const schedule = () => {
      if (rafId != null) return;
      rafId = requestAnimationFrame(() => {
        rafId = null;
        update();
      });
    };
    update();
    const vv = window.visualViewport;
    vv?.addEventListener('resize', schedule);
    vv?.addEventListener('scroll', schedule);
    window.addEventListener('resize', schedule);
    window.addEventListener('orientationchange', schedule);
    return () => {
      vv?.removeEventListener('resize', schedule);
      vv?.removeEventListener('scroll', schedule);
      window.removeEventListener('resize', schedule);
      window.removeEventListener('orientationchange', schedule);
      if (rafId != null) cancelAnimationFrame(rafId);
    };
  }, [isRecordingViewActive]);

  // Force video reflow when viewport changes (orientation change) — ensures preview re-lays out
  useEffect(() => {
    if (!isRecordingViewActive || !recordViewport.w) return;
    const video = videoRef.current;
    if (!video) return;
    try {
      video.play?.().catch(() => {});
      video.getBoundingClientRect();
    } catch {}
  }, [isRecordingViewActive, recordViewport]);

  // Debug: log container + video rects when recordViewport changes (rotation)
  useEffect(() => {
    if (!isRecordingViewActive || !recordViewport.w) return;
    const t = setTimeout(() => {
      const c = containerRef.current?.getBoundingClientRect();
      const v = videoRef.current;
      const vRect = v?.getBoundingClientRect();
      const vComputed = v ? getComputedStyle(v) : null;
      log('record_view_layout', {
        viewportPx: recordViewport,
        visualViewport: window.visualViewport
          ? { w: window.visualViewport.width, h: window.visualViewport.height }
          : 'n/a',
        innerWidth: window.innerWidth,
        innerHeight: window.innerHeight,
        containerRect: c ? { w: c.width, h: c.height } : null,
        videoRect: vRect ? { w: vRect.width, h: vRect.height } : null,
        videoComputed: vComputed ? { objectFit: vComputed.objectFit, transform: vComputed.transform } : null,
      });
    }, 50);
    return () => clearTimeout(t);
  }, [isRecordingViewActive, recordViewport]);

  // Ensure video plays when entering recording view
  useEffect(() => {
    if (isRecordingViewActive) {
      const playVideo = async () => {
        try {
          console.log('[RecordSubmission] Entering recording view, setting up stream...');
      // B5) Sampled draw loop log (~1s)
          const stream = await ensurePreviewStream();

          // Wait a tick for the video element to be rendered
          await new Promise(resolve => setTimeout(resolve, 50));

          if (videoRef.current) {
            console.log('[RecordSubmission] Setting srcObject and playing video...');
            videoRef.current.srcObject = stream;
            videoRef.current.muted = true;
            videoRef.current.playsInline = true;

            try {
              await videoRef.current.play();
              console.log('[RecordSubmission] Video playing successfully in recording view');
            } catch (playError) {
              console.error('[RecordSubmission] Play failed, retrying...', playError);
              await new Promise(resolve => setTimeout(resolve, 100));
              await videoRef.current.play();
            }
          } else {
            console.error('[RecordSubmission] videoRef.current is null after entering recording view');
          }
        } catch (e) {
          console.error('[RecordSubmission] Failed to play video in recording view:', e);
        }
      };
      playVideo();
    }
  }, [isRecordingViewActive]);

  // Keep live preview visible while recording
  useEffect(() => {
    if (!recording || recordedBlob) return;
    const el = videoRef.current;
    const stream = el?.srcObject as MediaStream | null;
    if (!stream) {
      ensurePreviewStream().catch(() => {});
      return;
    }
    el?.play?.().catch(() => {});
  }, [recording, recordedBlob]);

  const toggleMic = async () => {
    setMicOn((prev) => {
      const next = !prev;
      const stream = videoRef.current?.srcObject as MediaStream | null;
      stream?.getAudioTracks().forEach((track) => (track.enabled = next));
      return next;
    });
  };

  const toggleCam = async () => {
    setCamOn((prev) => {
      const next = !prev;
      const stream = videoRef.current?.srcObject as MediaStream | null;
      stream?.getVideoTracks().forEach((track) => (track.enabled = next));
      return next;
    });
  };

  // Keep facingModeRef and rotationAngleRef in sync for the canvas draw loop
  useEffect(() => {
    facingModeRef.current = facingMode;
      // B5) Sampled draw loop log (~1s)
  }, [facingMode]);
  useEffect(() => {
    rotationAngleRef.current = rotationAngle;
  }, [rotationAngle]);

  /**
   * Draw video → canvas every frame.
   *
   * The browser auto-rotates the video frame when the phone rotates, so drawImage()
   * always receives an upright frame. We do NOT need to counter-rotate or swap dimensions.
   * We just contain-fit the video into the locked canvas with consistent scaling.
   *
   * The canvas stays locked at the starting dimensions (e.g., 1280×2276 for portrait).
   * The browser-rotated frame is drawn with the same scale regardless of orientation,
   * producing natural letterboxing when the aspect ratio changes.
   */
  const startCanvasDrawLoop = () => {
    let lastGoodFrame: ImageData | null = null;

    const draw = () => {
      const video = videoRef.current;
      const canvas = canvasRef.current;
      if (!video || !canvas) return;
      if (video.readyState < 2) {
        if (lastGoodFrame) {
          const ctx = canvas.getContext('2d');
          if (ctx) ctx.putImageData(lastGoodFrame, 0, 0);
        }
        drawLoopRef.current = requestAnimationFrame(draw);
        return;
      }
      const vW = video.videoWidth;
      const vH = video.videoHeight;
      if (vW === 0 || vH === 0) {
        drawLoopRef.current = requestAnimationFrame(draw);
        return;
      }

      const cW = canvas.width;
      const cH = canvas.height;
      const ctx = canvas.getContext('2d');
      if (!ctx) return;

      // Clear frame every draw (prevents ghosting)
      ctx.setTransform(1, 0, 0, 1, 0, 0);
      ctx.clearRect(0, 0, cW, cH);
      ctx.fillStyle = '#000';
      ctx.fillRect(0, 0, cW, cH);

      // Simple contain-fit: scale video to fit within canvas, center it.
      // Same formula for both portrait and landscape — the browser already
      // rotated the frame, so vW/vH represent the upright content.
      const scale = Math.min(cW / vW, cH / vH);
      const drawW = vW * scale;
      const drawH = vH * scale;
      const drawX = (cW - drawW) / 2;
      const drawY = (cH - drawH) / 2;

      // RECDBG: ~1s snapshot
      if (RECDBG) {
        const now = performance.now();
        if (now - lastDrawLoopLogTsRef.current >= 1000) {
          lastDrawLoopLogTsRef.current = now;
          log('drawloop_sample', {
            sensorDims: { vW, vH },
            scale: Math.round(scale * 1000) / 1000,
            drawRect: { drawW: Math.round(drawW), drawH: Math.round(drawH), drawX: Math.round(drawX), drawY: Math.round(drawY) },
            canvas: { cW, cH },
            rotationAngle: rotationAngleRef.current,
          });
        }
      }

      try {
        ctx.save();
        if (facingModeRef.current === 'user') {
          ctx.translate(cW, 0);
          ctx.scale(-1, 1);
        }
        ctx.drawImage(video, drawX, drawY, drawW, drawH);
        ctx.restore();

        try {
          lastGoodFrame = ctx.getImageData(0, 0, cW, cH);
        } catch {
          /* getImageData can fail on tainted canvas */
        }
      } catch (e) {
        errLog('drawloop_drawImage_error', { error: String(e) });
      }
      drawLoopRef.current = requestAnimationFrame(draw);
    };
    draw();
  };

  const stopCanvasDrawLoop = () => {
    if (drawLoopRef.current != null) {
      cancelAnimationFrame(drawLoopRef.current);
      drawLoopRef.current = null;
    }
  };

  const startRecordingView = async () => {
    try {
      const stream = await ensurePreviewStream();
      const vv = window.visualViewport;
      console.log('[RecordSubmission] Starting recording view...');
      // B4) start() — orientation, dimensions, canvas, video rect
      dbgGroup(PREFIX, 'start recording', () => {
        const screenAngle = (window.screen as any).orientation?.angle;
        const video = videoRef.current;
        const canvas = canvasRef.current;
        dbg(PREFIX, 'start orientation', { rotationAngle, isLandscape });
        if (video && canvas) {
          dbg(PREFIX, 'start dimensions', {
            vW: video.videoWidth,
            vH: video.videoHeight,
            canvasW: canvas.width,
            canvasH: canvas.height,
          });
          dbg(PREFIX, 'start video rect', video.getBoundingClientRect());
        }
        dbg(PREFIX, 'start screen', {
          screenAngle: screenAngle ?? 'n/a',
          devicePixelRatio: window.devicePixelRatio,
        });
      });

      // Fullscreen + unlock BEFORE showing recording UI so orientation settles before user clicks Start.
      // Unlock can cause transient angle wobble; doing it first lets it settle.
      await requestFullscreenAndOrientationUnlock();

      log('startRecordingView_click', {
        isLandscape,
        rotationAngle,
        viewport: { innerW: window.innerWidth, innerH: window.innerHeight },
        visualViewport: vv ? { w: vv.width, h: vv.height, scale: vv.scale } : null,
      });
      setIsRecordingViewActive(true);
    } catch (e) {
      console.error('[RecordSubmission] Failed to start recording view:', e);
      alert('Failed to access camera. Please check permissions and try again.');
    }
  };

  // Request fullscreen and unlock screen orientation (overrides device rotation lock)
  const requestFullscreenAndOrientationUnlock = async () => {
    try {
      const isMobile = /Android|iPhone|iPad|iPod/i.test(navigator.userAgent);

      if (isMobile) {
        console.log('📱 [RecordSubmission] Requesting fullscreen for orientation control...');

        // Request fullscreen on the container
        const elem = containerRef.current || document.documentElement;

        try {
          if (elem.requestFullscreen) {
            await elem.requestFullscreen();
          } else if ((elem as any).webkitRequestFullscreen) {
            await (elem as any).webkitRequestFullscreen();
          } else if ((elem as any).mozRequestFullScreen) {
            await (elem as any).mozRequestFullScreen();
          } else if ((elem as any).msRequestFullscreen) {
            await (elem as any).msRequestFullscreen();
          }

          console.log('✅ [RecordSubmission] Fullscreen enabled');

          // Now try to unlock orientation (this should work in fullscreen)
          if ('screen' in window && 'orientation' in window.screen) {
            const screenOrientation = window.screen.orientation as any;

            if ('unlock' in screenOrientation) {
              try {
                const o = (window.screen as any).orientation;
                if (o && typeof o.unlock === 'function') {
                  try { o.unlock(); } catch {}
                }
                console.log('✅ [RecordSubmission] Orientation unlocked - rotation override active');
              } catch (e: any) {
                console.warn('⚠️ [RecordSubmission] Could not unlock orientation:', e);

                // ✅ Show helpful message if rotation is locked by device settings
                if (e.name === 'SecurityError' || e.message?.includes('lock')) {
                  toast({
                    title: 'Rotation lock',
                    description: 'Please disable rotation lock in your device settings for the best experience',
                  });
                }
              }
            }
          }
        } catch (fullscreenError: any) {
          console.warn('⚠️ [RecordSubmission] Fullscreen request failed:', fullscreenError.message);
          console.log('💡 [RecordSubmission] Continuing without fullscreen - sensor-based rotation will still work');
        }
      }
    } catch (error: any) {
      console.warn('⚠️ [RecordSubmission] Orientation setup failed:', error.message);
    }
  };

  const start = async () => {
    console.log('[RecordSubmission] Start clicked');
    try {
      const stream = await ensurePreviewStream();

      // @ts-ignore
      if (typeof MediaRecorder === 'undefined') {
        alert('Recording is not supported in this browser. Please try Chrome/Edge/Safari 14+.');
        return;
      }

      const canvas = canvasRef.current;
      if (!canvas) {
        console.error('[RecordSubmission] Canvas ref not available');
        alert('Recording setup failed. Please refresh and try again.');
        return;
      }

      // Ensure video/canvas have dimensions. Use stream settings as fallback.
      const video = videoRef.current;
      if (!video) return;
      const videoSettings = stream.getVideoTracks()[0]?.getSettings?.() || {};
      const fallbackW = videoSettings.width || 1280;
      const fallbackH = videoSettings.height || 720;
      if (video.videoWidth === 0 || video.videoHeight === 0) {
        await new Promise<void>((resolve) => {
          const onLoaded = () => resolve();
          video.addEventListener('loadedmetadata', onLoaded);
          setTimeout(() => {
            video.removeEventListener('loadedmetadata', onLoaded);
            resolve();
          }, 300);
        });
      }
      const vW = video.videoWidth || fallbackW;
      const vH = video.videoHeight || fallbackH;

      // Wait for orientation to settle after fullscreen/unlock (iOS Safari transient flips)
      await new Promise((r) => requestAnimationFrame(r as FrameRequestCallback));
      await new Promise((r) => setTimeout(r, 100));

      const baseAngle = snapTo90(rotationAngleRef.current ?? 0);
      baseAngleRef.current = baseAngle;
      stableAngleRef.current = baseAngle;
      pendingAngleRef.current = baseAngle;
      pendingSinceRef.current = performance.now();
      startedAtRef.current = performance.now();

      if (RECDBG) {
        const vv = window.visualViewport;
        log('record_start_orientation', {
          baseAngle,
          initialRawAngle: rotationAngleRef.current,
          vvDims: vv ? { w: vv.width, h: vv.height, scale: vv.scale } : null,
        });
      }

      // Canvas sizing: use camera sensor dimensions, swapped if user chose landscape.
      // iOS cameras always report sensor dims in native portrait (height > width),
      // so for landscape recordings we swap W/H to produce a wide video file.
      let canvasW = vW;
      let canvasH = vH;
      if (recordingLayout === 'landscape' && canvasH > canvasW) {
        [canvasW, canvasH] = [canvasH, canvasW];
      } else if (recordingLayout === 'portrait' && canvasW > canvasH) {
        [canvasW, canvasH] = [canvasH, canvasW];
      }
      const MIN_WIDTH = 1280;
      const MIN_HEIGHT = 720;
      if (canvasW < MIN_WIDTH || canvasH < MIN_HEIGHT) {
        const scaleUp = Math.max(MIN_WIDTH / canvasW, MIN_HEIGHT / canvasH);
        canvasW = Math.round(canvasW * scaleUp);
        canvasH = Math.round(canvasH * scaleUp);
      }
      canvas.width = canvasW;
      canvas.height = canvasH;
      console.log('[RecordSubmission] record_start_layout', {
        vW,
        vH,
        canvasW: canvas.width,
        canvasH: canvas.height,
        recordingLayout,
        baseAngle,
      });

      chunksRef.current = [];
      const t = pickType();
      const videoBitrate = Math.max(2500000, canvas.width * canvas.height * 30 * 0.1);
      log('record_start_snapshot', {
        vW,
        vH,
        fallbackW,
        fallbackH,
        computed: getVideoComputed(),
        canvas: { w: canvas.width, h: canvas.height },
        rects: getRects(),
        recorderMime: t,
        videoBitrate,
      });

      // Store audio track for the entire recording — we never replace it during flips.
      // This keeps a single continuous MediaRecorder stream.
      const audioTrack = stream.getAudioTracks()[0];
      recordingAudioRef.current = audioTrack ?? null;

      // Start drawing video → canvas every frame. When user flips camera, we only swap
      // video.srcObject; the canvas keeps drawing, so the recorder never stops.
      startCanvasDrawLoop();

      const canvasStream = canvas.captureStream(30);
      const recordStream = new MediaStream([
        ...canvasStream.getVideoTracks(),
        ...(recordingAudioRef.current ? [recordingAudioRef.current] : []),
      ]);

      let mr: MediaRecorder | null = null;
      try {
        const options = t.mime
          ? { mimeType: t.mime, videoBitsPerSecond: videoBitrate }
          : { videoBitsPerSecond: videoBitrate };
        mr = new MediaRecorder(recordStream, options as any);
        console.log('[RecordSubmission] MediaRecorder created (canvas) with bitrate:', videoBitrate);
      } catch (e1: any) {
        console.warn('[RecordSubmission] MediaRecorder with preferred type failed, retrying with default:', e1?.message || e1);
        try {
          mr = new MediaRecorder(recordStream);
        } catch (e2: any) {
          console.error('[RecordSubmission] Failed to create MediaRecorder:', e2);
          stopCanvasDrawLoop();
          alert(`Cannot start recorder: ${e2?.message || e2}`);
          return;
        }
      }

      mimeRef.current = t;
      recRef.current = mr;
      mr.ondataavailable = (e) => {
        if (e.data && e.data.size) {
          chunksRef.current.push(e.data);
          log('dataavailable', {
            size: e.data.size,
            type: e.data.type,
            mrState: mr.state,
            chunkCount: chunksRef.current.length,
          });
        }
      };

      mr.onstop = async () => {
        try {
          stopCanvasDrawLoop();
          recordingAudioRef.current = null;

          const inferredType = chunksRef.current[0]?.type || t.content;
          const combined = new Blob(chunksRef.current, { type: inferredType });
          const chunksCount = chunksRef.current.length;
          chunksRef.current = [];

          dbgGroup(PREFIX, 'stop recording', () => {
            dbg(PREFIX, 'onstop combined', {
              combinedType: combined.type,
              combinedSize: combined.size,
              chunksCount,
            });
          });

          setRecording(false);
          setRecordedBlob(combined);
          const url = URL.createObjectURL(combined);
          setRecordedUrl(url);

          // Persist to localStorage for crash recovery
          log('persist_attempt', { blobSize: combined.size, type: combined.type });
          try {
            const reader = new FileReader();
            reader.onloadend = () => {
              try {
                localStorage.setItem(`recording_${roomId}_${token}`, reader.result as string);
              } catch (storageErr: any) {
                const dataUrl = reader.result as string;
                errLog('persist_failed', {
                  name: storageErr?.name,
                  message: storageErr?.message,
                  dataUrlLength: typeof dataUrl === 'string' ? dataUrl.length : 0,
                });
                console.warn('Could not save recording to localStorage:', storageErr);
              }
            };
            reader.readAsDataURL(combined);
          } catch (e: any) {
            errLog('persist_failed', { name: e?.name, message: e?.message });
            console.warn('Could not persist recording:', e);
          }

          // Release camera / mic
          const liveStream = videoRef.current?.srcObject as MediaStream | null;
          liveStream?.getTracks().forEach((track) => {
            track.stop();
            console.log(`[RecordSubmission] ✅ Stopped ${track.kind} track after recording`);
          });
          if (activeStreamRef.current && activeStreamRef.current !== liveStream) {
            activeStreamRef.current.getTracks().forEach((track) => {
              track.stop();
              console.log(`[RecordSubmission] ✅ Stopped ${track.kind} track from activeStreamRef`);
            });
          }
          if (videoRef.current) videoRef.current.srcObject = null;
          activeStreamRef.current = null;
        } catch (e: any) {
          console.error('[RecordSubmission] onstop error:', e);
          setRecording(false);
          alert(`Could not prepare preview: ${e?.message || e}`);
        }
      };

      try {
        try { await video.play(); } catch {}
        mr.start(1000);
        console.log('[RecordSubmission] Recorder started (canvas — single continuous recording)');
        setRecording(true);
        setRecordingDuration(0);
        timerIntervalRef.current = setInterval(() => {
          setRecordingDuration(prev => prev + 1);
        }, 1000);
        setTimeout(() => {
          if (recRef.current === mr && chunksRef.current.length === 0 && mr.state === 'recording') {
            console.warn('[RecordSubmission] No dataavailable after 5s. This browser may not support this recording format.');
            alert('Recording seems unsupported in this browser. Please try the latest Chrome/Edge/Safari.');
          }
        }, 5000);
      } catch (e: any) {
        console.error('[RecordSubmission] mr.start failed:', e);
        stopCanvasDrawLoop();
        alert(`Cannot start recording: ${e?.message || e}`);
      }
    } catch (e: any) {
      console.error('[RecordSubmission] start error:', e);
      alert(e?.message || 'Unable to access camera/microphone');
    }
  };

  const stop = () => {
    if (timerIntervalRef.current) {
      clearInterval(timerIntervalRef.current);
      timerIntervalRef.current = null;
    }
    recRef.current?.stop();
  };

  const reRecord = async () => {
    try {
      console.log('[RecordSubmission] Re-recording - cleaning up previous recording...');

      // Clean up previous recording
      if (recordedUrl) URL.revokeObjectURL(recordedUrl);
      setRecordedUrl(null);
      setRecordedBlob(null);
      chunksRef.current = [];
      setRecordingDuration(0);
      setRecording(false);
      setIsRecordingViewActive(false);

      // Clear localStorage recording
      try {
        if (roomId && token) {
          localStorage.removeItem(`recording_${roomId}_${token}`);
        }
      } catch {}

      console.log('[RecordSubmission] Getting new media stream...');

      // Stop any existing tracks first
      const oldStream = videoRef.current?.srcObject as MediaStream | null;
      oldStream?.getTracks().forEach(track => {
        track.stop();
        console.log('[RecordSubmission] Stopped old track:', track.kind);
      });

      const stream = await getUserMediaWithQualityGuard(facingMode, videoDeviceId, audioDeviceId, 'reRecord');
      activeStreamRef.current = stream;
      applyTrackStates(stream);

      console.log('[RecordSubmission] New stream acquired:', {
        videoTracks: stream.getVideoTracks().length,
        audioTracks: stream.getAudioTracks().length
      });

      if (videoRef.current) {
        videoRef.current.pause?.();
        videoRef.current.removeAttribute('src');
        // @ts-ignore
        videoRef.current.src = '';
        videoRef.current.srcObject = stream;
        videoRef.current.controls = false;
        videoRef.current.muted = true;
        videoRef.current.playsInline = true;

        try {
          await videoRef.current.play();
          console.log('[RecordSubmission] Video playback started');
        } catch (playError) {
          console.error('[RecordSubmission] Play failed:', playError);
        }
      }
    } catch (e) {
      console.error('[RecordSubmission] reRecord failed:', e);
      alert('Failed to access camera. Please check permissions and try again.');
    }
  };

  const submitRecording = async () => {
    if (!recordedBlob) return;
    if (!roomId) return alert('Missing room');
    setSubmitting(true);
    console.log('[RecordSubmission] Starting submission...', { roomId, token, blobSize: recordedBlob.size });
    try {
      // Always save as MP4 format
      const inferredType = recordedBlob.type || 'video/mp4';
      const ts = new Date().toISOString().replace(/[:.]/g, '-');
      const ext = 'mp4'; // Force MP4 extension
      const path = `${roomId}/guest-${token}-${ts}.${ext}`;

      // Convert to MP4 if needed
      let uploadBlob = recordedBlob;
      if (!inferredType.includes('mp4')) {
        uploadBlob = new Blob([recordedBlob], { type: 'video/mp4' });
      }

      console.log('[RecordSubmission] Uploading to storage:', path);
      const { error } = await supabase.storage
        .from('call-recordings')
        .upload(path, uploadBlob, { contentType: 'video/mp4', upsert: false });
      if (error) {
        console.error('[RecordSubmission] Storage upload error:', error);
        throw error;
      }
      console.log('[RecordSubmission] Storage upload successful');

      // Submit recording via secure backend endpoint
      // This approach prevents guests from having direct Supabase database access
      const base = import.meta.env.VITE_SIGNALING_SERVER_URL ?? 'http://localhost:3000';
      const guestPhone = token; // token is derived from the room ID

      console.log('[RecordSubmission] Submitting recording to backend:', { roomId, guestPhone, path });

      const response = await fetch(`${base}/api/guest-recording/submit`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          roomId,
          guestPhone,
          storagePath: path,
          durationSeconds: recordingDuration || null,
          fileSizeBytes: uploadBlob.size,
          mimeType: 'video/mp4'
        })
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ error: 'Unknown error' }));

        // Show user-friendly error message for duplicate submissions
        if (response.status === 409) {
          throw new Error('You have already submitted a recording for this call. Thank you!');
        }

        throw new Error(errorData.error || 'Failed to submit recording');
      }

      const result = await response.json();
      console.log('✅ Recording submitted successfully:', result);

      // Clear localStorage after successful submission
      try {
        localStorage.removeItem(`recording_${roomId}_${token}`);
      } catch {}

      // Show success state first
      setSubmitting(false);
      setSubmissionSuccess(true);

      // ✅ CRITICAL: Release camera and microphone immediately after submission
      // This prevents camera/mic staying on until the referral modal is dismissed
      // Do this BEFORE showing any UI to ensure permissions are released
      try {
        // Use activeStreamRef which persists across view changes
        const stream = activeStreamRef.current;
        if (stream) {
          console.log('[RecordSubmission] 🎥 Releasing camera and microphone tracks...');
          stream.getTracks().forEach((track) => {
            track.stop();
            console.log(`[RecordSubmission] ✅ Stopped ${track.kind} track - readyState:`, track.readyState);
          });
          activeStreamRef.current = null; // Clear the ref
          console.log('[RecordSubmission] ✅ All media tracks released');
        } else {
          console.warn('[RecordSubmission] ⚠️ No active stream to release');
        }

        // Also clear video element if it exists
        const videoElement = videoRef.current;
        if (videoElement) {
          videoElement.pause?.();
          videoElement.srcObject = null;
          videoElement.removeAttribute('src');
          // @ts-ignore
          videoElement.src = '';
          console.log('[RecordSubmission] ✅ Cleared video element');
        }
      } catch (cleanupError) {
        console.error('[RecordSubmission] ❌ Error releasing media tracks:', cleanupError);
      }

      // Show referral prompt after a brief delay (500ms instead of 1000ms)
      setTimeout(() => {
        console.log('[RecordSubmission] 🎁 Showing referral prompt');
        setShowReferralPrompt(true);
      }, 500);
    } catch (e: any) {
      console.error('[RecordSubmission] Full error:', e);
      const errorMessage = e?.message || JSON.stringify(e) || String(e);
      alert(`Upload failed: ${errorMessage}`);
      setSubmitting(false);
    }
  };

  if (loading) return <div className="min-h-screen flex items-center justify-center text-white bg-black">Loading…</div>;
  if (err) return <div className="min-h-screen flex items-center justify-center text-white bg-black">Error: {err}</div>;

  // Success screen after submission - only show referral modal, no duplicate thank you page
  if (submissionSuccess) {
    return (
      <>
        {/* Show PostCallReferralPrompt immediately - no inline thank you screen */}
        {showReferralPrompt ? (
          <PostCallReferralPrompt
            onClose={() => {
              setShowReferralPrompt(false);
              // Redirect to thank you page after closing prompt
              window.location.href = '/thank-you';
            }}
            guestName={guestInfo.name}
            guestEmail={guestInfo.email}
            guestPhone={guestInfo.phone}
          />
        ) : (
          // Brief loading state while waiting for referral modal (1 second delay)
          <div className="flex items-center justify-center min-h-screen bg-white">
            <div className="max-w-md w-full px-6 py-8">
              <div className="text-center mb-8">
                {/* App Icon instead of checkmark */}
                <div className="w-20 h-20 mx-auto mb-6 flex items-center justify-center">
                  <img
                    src="/wlkthruicon.png"
                    alt="Wlkthru"
                    className="w-20 h-20 object-contain"
                  />
                </div>
                <h1 className="text-2xl font-semibold text-gray-900 mb-3">
                  Video Submitted Successfully!
                </h1>
                <p className="text-gray-600 mb-1">
                  Thank you for your submission
                </p>
              </div>
            </div>
          </div>
        )}
      </>
    );
  }

  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  // Full-screen recording view - shown after clicking "Start Recording"
  if (isRecordingViewActive && !recordedBlob) {
    const w = recordViewport.w || (typeof window !== 'undefined' ? (window.visualViewport?.width ?? window.innerWidth) : 0);
    const h = recordViewport.h || (typeof window !== 'undefined' ? (window.visualViewport?.height ?? window.innerHeight) : 0);
    const recContainerStyle: CSSProperties = {
      position: 'fixed',
      top: 0,
      left: 0,
      overflow: 'hidden',
      backgroundColor: 'black',
      width: `${w}px`,
      height: `${h}px`,
    };
    return (
      <div
        ref={containerRef}
        className="bg-black text-white"
        style={recContainerStyle}
      >
        <video
          ref={videoRef}
          className="absolute inset-0 w-full h-full"
          autoPlay
          playsInline
          muted
          style={{
            objectFit: RECORD_PREVIEW_OBJECT_FIT,
            transform: facingMode === 'user' ? 'scaleX(-1)' : 'scaleX(1)',
          }}
        />
        {/* Hidden canvas for continuous recording — recorder never stops on camera flip */}
        <canvas
          ref={canvasRef}
          style={{ position: 'absolute', left: -9999, width: 1, height: 1, pointerEvents: 'none' }}
          aria-hidden
        />

        {/* Top Controls - Mic and Camera */}
        <div className="absolute top-4 left-4 right-4 flex gap-3 z-20" style={{ paddingTop: 'env(safe-area-inset-top, 0px)' }}>
          <button
            onClick={toggleMic}
            className={`w-12 h-12 rounded-full flex items-center justify-center text-white transition ${micOn ? 'bg-black/70 hover:bg-black/80' : 'bg-red-600 hover:bg-red-700'}`}
            aria-label="Toggle microphone"
          >
            {micOn ? <Mic className="w-5 h-5" /> : <MicOff className="w-5 h-5" />}
          </button>
          <button
            onClick={toggleCam}
            className={`w-12 h-12 rounded-full flex items-center justify-center text-white transition ${camOn ? 'bg-black/70 hover:bg-black/80' : 'bg-red-600 hover:bg-red-700'}`}
            aria-label="Toggle camera"
          >
            {camOn ? <Video className="w-5 h-5" /> : <VideoOff className="w-5 h-5" />}
          </button>
        </div>

        {/* Recording Timer - Show only when recording */}
        {recording && (
          <div className="absolute top-4 right-4 z-20" style={{ paddingTop: 'env(safe-area-inset-top, 0px)' }}>
            <div className="px-3 py-1.5 rounded-full bg-black/50 backdrop-blur-sm flex items-center gap-2">
              <div className="w-2 h-2 bg-red-500 rounded-full animate-pulse" />
              <span className="text-white font-mono text-sm font-medium tabular-nums">
                {formatTime(recordingDuration)}
              </span>
            </div>
          </div>
        )}

        {/* Bottom Controls */}
        <div
          className="absolute left-0 right-0 z-20 px-6 flex items-center justify-between"
          style={{ bottom: 'calc(2rem + env(safe-area-inset-bottom, 20px))' }}
        >
          {/* Left - Switch Camera */}
          <button
            onClick={switchCamera}
            className="w-14 h-14 rounded-full flex items-center justify-center bg-white/20 backdrop-blur-sm active:bg-white/30 transition-all shadow-lg"
            aria-label="Switch camera"
          >
            <SwitchCamera className="w-6 h-6 text-white" strokeWidth={2} />
          </button>

          {/* Center - Record/Stop Button */}
          {!recording ? (
            // Start Recording - Red circle button
            <button
              onClick={start}
              className="w-20 h-20 rounded-full flex items-center justify-center bg-red-600 hover:bg-red-700 active:bg-red-800 shadow-2xl active:scale-95 transition-all"
              aria-label="Start recording"
            >
              <div className="w-16 h-16 bg-red-600 rounded-full" />
            </button>
          ) : (
            // Stop Recording - Red square inside white circle
            <button
              onClick={stop}
              className="w-20 h-20 rounded-full flex items-center justify-center bg-white border-4 border-white shadow-2xl active:scale-95 transition-transform"
              aria-label="Stop recording"
            >
              <div className="w-7 h-7 bg-red-600 rounded-sm" />
            </button>
          )}

          {/* Right - Redo or Done Button */}
          {recording ? (
            // Done button while recording
            <button
              onClick={stop}
              className="w-14 h-14 rounded-full flex items-center justify-center bg-white/20 backdrop-blur-sm active:bg-white/30 transition-all shadow-lg"
              aria-label="Done"
            >
              <Check className="w-7 h-7 text-white" strokeWidth={3} />
            </button>
          ) : (
            // Redo button when not recording (to restart fresh)
            <button
              onClick={reRecord}
              className="w-14 h-14 rounded-full flex items-center justify-center bg-white/20 backdrop-blur-sm active:bg-white/30 transition-all shadow-lg"
              aria-label="Start fresh"
            >
              <RotateCw className="w-6 h-6 text-white" strokeWidth={2} />
            </button>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-blue-50" style={{ overflow: 'visible' }}>
      {/* ✅ 100dvh for iOS rotation; landscape: rules for proper orientation */}
      <div
        ref={containerRef}
        className="flex items-center justify-center p-4"
        style={{
          width: '100%',
          height: '100dvh',
          minHeight: '100dvh',
        }}
      >
      <div className="w-full max-w-4xl landscape:max-w-full landscape:flex landscape:flex-col landscape:min-h-0">
        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="text-3xl md:text-4xl font-bold mt-10 text-gray-900 mb-2">
            Record Your Message
          </h1>
          <p className="text-gray-600">Set up your audio and video before recording</p>
        </div>

        <div className="bg-white rounded-3xl shadow-2xl overflow-hidden border border-gray-100 landscape:flex-1 landscape:min-h-0">
          <div className="grid md:grid-cols-2 gap-0 landscape:grid-cols-1 landscape:flex landscape:flex-col landscape:min-h-0">
            {/* Video Preview — portrait: aspect-video; landscape: flex to fill, object-contain */}
            <div className="relative bg-gray-900 aspect-video landscape:aspect-auto landscape:flex-1 landscape:min-h-[40dvh]">
              {recordedUrl ? (
                <video
                  ref={recordedVideoRef}
                  key="recorded"
                  className="w-full h-full object-contain"
                  controls
                  playsInline
                  src={recordedUrl}
                  style={{
                    transform: 'none',
                  }}
                  onLoadedMetadata={() => {
                    const v = recordedVideoRef.current;
                    if (!v) return;
                    const r = v.getBoundingClientRect();
                    const s = getComputedStyle(v);
                    log('recorded_preview_loadedmetadata', {
                      videoWidth: v.videoWidth,
                      videoHeight: v.videoHeight,
                      rect: { w: r.width, h: r.height },
                      computed: { transform: s.transform, objectFit: s.objectFit },
                    });
                  }}
                />
              ) : (
                <video
                  key="live"
                  ref={videoRef}
                  className="w-full h-full object-contain"
                  autoPlay
                  playsInline
                  muted
                  controls={false}
                  disablePictureInPicture
                  disableRemotePlayback
                  controlsList="nodownload nofullscreen noremoteplayback"
                  style={{
                    // E) Keep only front camera mirroring; rotation handled by canvas recording
                    transform: facingMode === 'user' ? 'scaleX(-1)' : 'none',
                    pointerEvents: 'none'
                  }}
                  // @ts-ignore - webkit specific attributes
                  webkit-playsinline="true"
                  x5-playsinline="true"
                  x5-video-player-type="h5"
                  x5-video-player-fullscreen="false"
                />
              )}

              {!camOn && !recordedUrl && (
                <div className="absolute inset-0 flex items-center justify-center bg-gray-800">
                  <div className="text-center">
                    <div className="w-20 h-20 bg-gray-700 rounded-full flex items-center justify-center mx-auto mb-3">
                      <VideoOff className="h-10 w-10 text-gray-400" />
                    </div>
                    <p className="text-gray-400">Camera is off</p>
                  </div>
                </div>
              )}

              {/* Preview Controls */}
              {!recordedBlob && (
                <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex gap-3">
                  <button
                    onClick={toggleMic}
                    className={`w-12 h-12 rounded-full flex items-center justify-center transition-all shadow-lg ${
                      micOn
                        ? 'bg-white/20 backdrop-blur-sm hover:bg-white/30'
                        : 'bg-red-500 hover:bg-red-600'
                    }`}
                  >
                    {micOn ? (
                      <Mic className="h-5 w-5 text-white" strokeWidth={2} />
                    ) : (
                      <MicOff className="h-5 w-5 text-white" strokeWidth={2} />
                    )}
                  </button>

                  <button
                    onClick={toggleCam}
                    className={`w-12 h-12 rounded-full flex items-center justify-center transition-all shadow-lg ${
                      camOn
                        ? 'bg-white/20 backdrop-blur-sm hover:bg-white/30'
                        : 'bg-red-500 hover:bg-red-600'
                    }`}
                  >
                    {camOn ? (
                      <Video className="h-5 w-5 text-white" strokeWidth={2} />
                    ) : (
                      <VideoOff className="h-5 w-5 text-white" strokeWidth={2} />
                    )}
                  </button>

                  <button
                    onClick={() => setShowDeviceSettings(!showDeviceSettings)}
                    className="w-12 h-12 rounded-full flex items-center justify-center bg-white/20 backdrop-blur-sm hover:bg-white/30 transition-all shadow-lg"
                  >
                    <Settings className="h-5 w-5 text-white" strokeWidth={2} />
                  </button>
                </div>
              )}
            </div>

            {/* Recording Form */}
            <div className="p-8 md:p-10 flex flex-col justify-center">
              <div className="space-y-6">
                {/* Device Settings */}
                {showDeviceSettings && !recordedBlob && (
                  <div className="space-y-4 animate-in slide-in-from-top-2">
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">
                        Camera
                      </label>
                      <select
                        value={videoDeviceId}
                        onChange={(e) => setVideoDeviceId(e.target.value)}
                        className="w-full px-4 py-3 text-base border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white"
                        style={{ fontSize: '16px' }}
                      >
                        <option value="">Default camera</option>
                        {videoDevices.map((device) => (
                          <option key={device.deviceId} value={device.deviceId}>
                            {device.label || `Camera ${device.deviceId.slice(0, 8)}`}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">
                        Microphone
                      </label>
                      <select
                        value={audioDeviceId}
                        onChange={(e) => setAudioDeviceId(e.target.value)}
                        className="w-full px-4 py-3 text-base border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white"
                        style={{ fontSize: '16px' }}
                      >
                        <option value="">Default microphone</option>
                        {audioDevices.map((device) => (
                          <option key={device.deviceId} value={device.deviceId}>
                            {device.label || `Microphone ${device.deviceId.slice(0, 8)}`}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                )}

                {/* Recording Preview Success */}
                {recordedBlob && (
                  <>
                    <div className="p-4 bg-green-50 rounded-lg border border-green-200">
                      <p className="font-semibold text-green-900">Recording Complete</p>
                      <p className="text-sm text-green-800">Preview your video and submit when ready</p>
                    </div>

                    {/* Warning about page refresh */}
                    <div className="p-4 bg-amber-50 rounded-lg border border-amber-200">
                      <p className="text-sm font-semibold text-amber-900 mb-1">Important</p>
                      <p className="text-sm text-amber-800">
                        Don't refresh this page or your recording will be lost. Please click "Submit Video" to upload your recording.
                      </p>
                    </div>
                  </>
                )}

                {/* Terms and Conditions */}
                {!recordedBlob && (
                  <div className="flex items-start gap-3 p-4 bg-gray-50 rounded-xl border border-gray-200">
                    <input
                      type="checkbox"
                      id="terms-checkbox"
                      checked={termsAccepted}
                      onChange={(e) => setTermsAccepted(e.target.checked)}
                      className="mt-1 w-5 h-5 text-blue-600 border-gray-300 rounded focus:ring-blue-500 focus:ring-2 cursor-pointer"
                    />
                    <label htmlFor="terms-checkbox" className="flex-1 text-sm text-gray-700 cursor-pointer">
                      I agree to the{' '}
                      <a
                        href="/legal?section=terms"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-blue-600 hover:text-blue-700 underline font-medium"
                        onClick={(e) => e.stopPropagation()}
                      >
                        Terms of Service
                      </a>
                      {' '}and{' '}
                      <a
                        href="/legal?section=privacy"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-blue-600 hover:text-blue-700 underline font-medium"
                        onClick={(e) => e.stopPropagation()}
                      >
                        Privacy Policy
                      </a>
                      . I consent to this recording being used for the intended purpose.
                    </label>
                  </div>
                )}

                {/* Recording Layout Selector */}
                {!recordedBlob && (
                  <div className="space-y-2">
                    <label className="block text-sm font-semibold text-gray-700">
                      Recording Layout
                    </label>
                    <div className="grid grid-cols-2 gap-3">
                      <button
                        type="button"
                        onClick={() => setRecordingLayout('portrait')}
                        className={`flex flex-col items-center gap-2 p-4 rounded-xl border-2 transition-all ${
                          recordingLayout === 'portrait'
                            ? 'border-[hsl(var(--wlkthru-purple))] bg-[hsl(var(--wlkthru-purple))]/5'
                            : 'border-gray-200 hover:border-gray-300'
                        }`}
                      >
                        <div className={`w-8 h-12 rounded-sm border-2 ${
                          recordingLayout === 'portrait' ? 'border-[hsl(var(--wlkthru-purple))]' : 'border-gray-400'
                        }`} />
                        <span className={`text-sm font-medium ${
                          recordingLayout === 'portrait' ? 'text-[hsl(var(--wlkthru-purple))]' : 'text-gray-600'
                        }`}>Portrait</span>
                      </button>
                      <button
                        type="button"
                        onClick={() => setRecordingLayout('landscape')}
                        className={`flex flex-col items-center gap-2 p-4 rounded-xl border-2 transition-all ${
                          recordingLayout === 'landscape'
                            ? 'border-[hsl(var(--wlkthru-purple))] bg-[hsl(var(--wlkthru-purple))]/5'
                            : 'border-gray-200 hover:border-gray-300'
                        }`}
                      >
                        <div className={`mt-[10%] w-12 h-8 rounded-sm border-2 ${
                          recordingLayout === 'landscape' ? 'border-[hsl(var(--wlkthru-purple))]' : 'border-gray-400'
                        }`} />
                        <span className={`text-sm font-medium ${
                          recordingLayout === 'landscape' ? 'text-[hsl(var(--wlkthru-purple))]' : 'text-gray-600'
                        }`}>Landscape</span>
                      </button>
                    </div>
                  </div>
                )}

                {/* Action Buttons */}
                {!recordedBlob && (
                  <Button
                    onClick={startRecordingView}
                    disabled={!termsAccepted}
                    className="w-full py-6 text-lg font-semibold bg-[hsl(var(--wlkthru-purple))] hover:bg-[hsl(var(--wlkthru-purple))]/90 active:bg-[hsl(var(--wlkthru-purple))]/80 disabled:bg-gray-300 rounded-xl shadow-lg transition-all disabled:cursor-not-allowed text-white"
                  >
                    Start Recording
                  </Button>
                )}

                {recordedBlob && (
                  <div className="space-y-3">
                    <Button
                      variant="outline"
                      onClick={() => setShowRecordAgainConfirm(true)}
                      className="w-full py-6 text-lg font-semibold rounded-xl border-2"
                    >
                      Record Again
                    </Button>
                    <AlertDialog open={showRecordAgainConfirm} onOpenChange={setShowRecordAgainConfirm}>
                      <AlertDialogContent>
                        <AlertDialogHeader>
                          <AlertDialogTitle>Record again?</AlertDialogTitle>
                          <AlertDialogDescription>
                            Are you sure you want to record again? Your current recording will be discarded and you will need to record a new video.
                          </AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter>
                          <AlertDialogCancel>No, keep it</AlertDialogCancel>
                          <AlertDialogAction
                            onClick={() => {
                              setShowRecordAgainConfirm(false);
                              reRecord();
                            }}
                            className="bg-[hsl(var(--wlkthru-purple))] hover:bg-[hsl(var(--wlkthru-purple))]/90"
                          >
                            Yes, record again
                          </AlertDialogAction>
                        </AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>
                    <Button
                      onClick={submitRecording}
                      disabled={submitting}
                      className="w-full py-6 text-lg font-semibold bg-green-600 hover:bg-green-700 active:bg-green-800 disabled:bg-gray-300 rounded-xl shadow-lg transition-all disabled:cursor-not-allowed text-white"
                    >
                      {submitting ? 'Submitting…' : 'Submit Video'}
                    </Button>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
      </div> {/* End of rotatable container */}
    </div>
  );
}